export interface PollEditMarkupIds {
    sectionLabelInput: string;
    addSectionButton: string;
    addQuestionButton: string;
}

export interface PollInsertDropdownMarkup {
    toggleLabel: string;
    /** View model handler, called with the chosen dynamic-placement flag. */
    clickHandler: string;
    /** Leads the option tooltips, e.g. "Insert" or "Save and Insert". */
    titlePrefix: string;
}

export interface PollEditInfoMarkup {
    titleInputId: string;
    datePickerContainerId: string;
    datePickerLabelFor: string;
    brandTagsContainerId: string;
    selectedArticleContainerId: string;
    selectedDiscussionArticleContainerId: string;
    datePickerHtml: string;
    brandsFilterHtml: string;
    referenceArticleHtml: string;
    discussionArticleHtml: string;
}

/**
 * Markup shared by the standalone poll screens (pollsView/pollsEdit) and their
 * counterparts inside the article poll picker, so both stay visually identical.
 */
export class PollMarkup {

    protected static readonly COMMERCIAL_ICON: string = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1h-3zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5"/></svg>`;

    public static sectionHeading(title: string, extraStyle: string = ""): string {
        return /* html */ `
            <div class="w-form-body">
                <div class="form-body full-width" style="border-bottom: 0${extraStyle}">
                    <strong class="form-sub-title" style="margin-bottom: 0">${title}</strong>
                </div>
            </div>`;
    }

    /**
     * Read-only poll summary: identity on the left, dates on the right.
     */
    public static viewInfoPanel(extraRightRows: string = ""): string {
        return /* html */ `
            <div class="w-form-body poll-view-info">
                <div class="form-body poll-view-info-left">
                    <div class="form-group">
                        <dl class="double-list margin">
                            <dt class="full-width"><span>Name</span></dt>
                            <dd class="full-width"><span data-bind="text: name"></span></dd>
                        </dl>
                    </div>
                    <div class="form-group">
                        <dl class="double-list margin">
                            <dt class="full-width"><span>Brands</span></dt>
                            <dd class="full-width"><span data-bind="text: attachedBrands() || 'None'"></span></dd>
                        </dl>
                    </div>
                    <div class="form-group" data-bind="visible: referenceArticleTitle">
                        <dl class="double-list margin">
                            <dt class="full-width"><span>Reference Article</span></dt>
                            <dd class="full-width"><span data-bind="text: referenceArticleTitle"></span></dd>
                        </dl>
                    </div>
                    <div class="form-group" data-bind="visible: discussionArticleTitle">
                        <dl class="double-list margin">
                            <dt class="full-width"><span>Discussion Article</span></dt>
                            <dd class="full-width"><span data-bind="text: discussionArticleTitle"></span></dd>
                        </dl>
                    </div>
                </div>

                <div class="form-body poll-view-info-right">
                    ${extraRightRows}
                    <div class="form-group">
                        <dl class="double-list margin">
                            <dt class=""><span>Close Date</span></dt>
                            <dd class=""><span data-bind="text: dateClosed()||'Not Selected'"></span></dd>
                        </dl>
                    </div>
                    <div class="form-group">
                        <dl class="double-list margin">
                            <dt class=""><span>Date Created</span></dt>
                            <dd class=""><span data-bind="text: dateCreated()||'Not Selected'"></span></dd>
                        </dl>
                    </div>
                </div>
            </div>`;
    }

    /**
     * Whether the widget inserted in the content shows the poll title. It belongs to the placeholder,
     * not to the poll, so both screens that can insert one carry the same checkbox.
     */
    public static displayTitleCheckbox(inputId: string): string {
        return /* html */ `
            <div class="form-check-inline">
                <input id="${inputId}" type="checkbox" data-bind="checked: isWithTitle">
                <label for="${inputId}" class="form-label icon i-check"><span>Display Poll Title</span></label>
            </div>`;
    }

    /**
     * Split button offering the two placement modes a poll can be inserted with.
     */
    public static insertDropdown(markup: PollInsertDropdownMarkup): string {
        const {toggleLabel, clickHandler, titlePrefix} = markup;

        return /* html */ `
            <div class="insert-dropdown" data-bind="css: { 'is-open': isInsertDropdownOpen }">
                <button class="btn-icon insert-dropdown-toggle i-dropdown-simple icon i-ins" type="button" data-bind="event: { click: onClickToggleInsertDropdown }"><span>${toggleLabel}</span></button>
                <div class="insert-dropdown-list">
                    <button class="btn-icon icon i-ins" type="button" data-bind="event: { click: ${clickHandler}.bind($data, true) }" title="${titlePrefix} with Dynamic Placement"><span>Dynamic Using Sentinel Avg. Scroll Depth %</span></button>
                    <button class="btn-icon icon i-ins" type="button" data-bind="event: { click: ${clickHandler}.bind($data, false) }" title="${titlePrefix} without Dynamic Placement"><span>Static Placement</span></button>
                </div>
            </div>`;
    }

    public static viewInfoRow(label: string, valueBinding: string): string {
        return /* html */ `
            <div class="form-group">
                <dl class="double-list margin">
                    <dt class=""><span>${label}</span></dt>
                    <dd class=""><span data-bind="text: ${valueBinding}"></span></dd>
                </dl>
            </div>`;
    }

    /**
     * Brand selector + CSV export, shown above the results.
     */
    public static viewToolbar(brandFilterHtml: string): string {
        return /* html */ `
            <div class="w-form-body w-justify poll-view-toolbar">
                <div class="w-right">
                    ${brandFilterHtml}
                    <button data-bind="click : onClickExportToCSV , css: { 'is-disable': exportInProgress }, text : exportTextBox" class="btn secondary icon i-export" type="button">Export to CSV</button>
                </div>
            </div>`;
    }

    public static emptyQuestions(): string {
        return /* html */ `
            <div class="w-form-body poll-empty-questions" data-bind="visible: questions().length == 0">
                <div class="form-body full-width">
                    <div class="form-group">
                        <p class="form-text">This poll has no questions.</p>
                    </div>
                </div>
            </div>`;
    }

    /**
     * Per-question results card with the vote bars and commercial columns.
     */
    public static questionViewCards(): string {
        return /* html */ `
            <div data-bind="foreach: questions">
                <div class="w-form-body question-view-card">
                    <strong class="form-sub-title poll-question-title">
                        <span data-bind="text: question"></span>
                    </strong>
                    <div class="poll-question-meta">
                        <div class="poll-meta-item">
                            <span class="poll-meta-label">Question Section:</span>
                            <strong data-bind="text: section"></strong>
                        </div>
                        <div class="poll-meta-item">
                            <span class="poll-meta-label">Question Type:</span>
                            <strong data-bind="text: type"></strong>
                        </div>
                        <div class="poll-meta-item">
                            <span class="poll-meta-label">Total Voters:</span>
                            <strong data-bind="text: votes"></strong>
                        </div>
                    </div>
                    <div class="poll-results">
                        <div class="poll-results-header">
                            <strong>Answers</strong>
                            <strong>Commercial Signal / Entity / Value</strong>
                        </div>
                        <div class="poll-results-list" data-bind="foreach: choices">
                            <div class="poll-result-row">
                                <span class="poll-answer" data-bind="text: choiceLabel || choiceText"></span>
                                <div class="poll-result-summary">
                                    <span class="w-result-bar">
                                        <span class="result-bar" data-bind="style: { width: (percentage || 0) + '%' }"></span>
                                    </span>
                                    <strong class="poll-result-count" data-bind="text: (percentage || 0) + '%, ' + (votes || 0) + ' Votes'"></strong>
                                </div>
                                <div class="poll-commercial-data">
                                    <span data-bind="text: $data.commercialSignal || ''"></span>
                                    <span data-bind="text: $data.commercialEntity || ''"></span>
                                    <span data-bind="text: $data.commercialValue || ''"></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    /**
     * Editable poll identity: title, close date, brands and the source article.
     */
    public static editInfoPanel(markup: PollEditInfoMarkup): string {
        return /* html */ `
            <div class="w-form-body quiz-picker-scroll poll-info-edit-layout quiz-edit-layout">
                <div class="w-poll-body quiz-picker-info-section">
                    <div class="w-form-body w-poll-sections-list">
                        <div class="form-body poll-info-section">
                            <div class="poll-info-row">
                                <div class="form-group poll-info-field poll-info-field--title">
                                    <label for="${markup.titleInputId}" class="form-label quiz-picker-section-heading__label">Title</label>
                                    <input id="${markup.titleInputId}" data-bind="value: name, valueUpdate: 'keyup'" type="text" class="input-text">
                                </div>
                                <div id="${markup.datePickerContainerId}" class="poll-info-field poll-info-field--close-date poll-close-date-group align-left">
                                    <label class="form-label" for="${markup.datePickerLabelFor}">Close Date</label>
                                    ${markup.datePickerHtml}
                                </div>
                            </div>

                            <div class="form-group quiz-picker-brands-row">
                                <div class="quiz-picker-brands-filter">
                                    ${markup.brandsFilterHtml}
                                </div>
                                <div id="${markup.brandTagsContainerId}" class="quiz-picker-brand-tags"></div>
                            </div>
                        </div>

                        <div class="quiz-picker-article-section">
                            <div class="form-group quiz-picker-section-heading">
                                <label class="form-label quiz-picker-section-heading__label">Source Article</label>
                            </div>
                            <div class="form-body quiz-picker-article-panel">
                                <div class="form-group quiz-picker-article-search">
                                    <div class="quiz-picker-article-search__field">
                                        ${markup.referenceArticleHtml}
                                    </div>
                                </div>

                                <div class="w-filters-selected quiz-picker-selected-articles" data-bind="visible: hasReferenceArticle()">
                                    <strong>Selected Articles:</strong>
                                    <div id="${markup.selectedArticleContainerId}" class="filters-selected"></div>
                                </div>

                                <div class="form-group quiz-picker-generate-actions">
                                    <button class="btn secondary" type="button" data-bind="click: onClickGenerateBasedOnArticle, disable: !hasReferenceArticle() || dateGeneratedFromArticle()">Generate from Article</button>
                                    <small data-bind="visible: dateGeneratedFromArticle">This article is locked because it was used to generate the poll.</small>
                                </div>
                            </div>
                        </div>

                        <div class="quiz-picker-article-section">
                            <div class="form-group quiz-picker-section-heading">
                                <label class="form-label quiz-picker-section-heading__label">Discussion Article<span data-bind="text: discussionArticleBrandName() ? ' (' + discussionArticleBrandName() + ')' : ''"></span></label>
                            </div>
                            <div class="form-body quiz-picker-article-panel">
                                <div class="form-group quiz-picker-article-search">
                                    <div class="quiz-picker-article-search__field">
                                        ${markup.discussionArticleHtml}
                                    </div>
                                    <small>Hosts the discussion for this brand only; the other brands keep their own.</small>
                                </div>

                                <div class="w-filters-selected quiz-picker-selected-articles" data-bind="visible: hasDiscussionArticle()">
                                    <strong>Selected Article:</strong>
                                    <div id="${markup.selectedDiscussionArticleContainerId}" class="filters-selected"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    /**
     * "Add a Section" row, the section pills and the questions toolbar.
     */
    public static editSectionsAndToolbar(ids: PollEditMarkupIds): string {
        return /* html */ `
            <div class="w-form-body w-poll-add-section">
                <div class="form-body">
                    <div class="form-group poll-add-section-group">
                        <label class="form-label">+ Add a Section</label>
                        <div class="poll-section-actions">
                            <div class="row" style="min-width: 50%;">
                                <input id="${ids.sectionLabelInput}" type="text" class="input-text" style="flex:1;margin-left: 0" data-bind="value: pollSectionLabel, valueUpdate: 'keyup'">
                                <button id="${ids.addSectionButton}" class="btn border icon only-icon i-add" data-bind="event: {click: onClickAddPollSection}, disable: pollSectionLabel() == ''" type="button"></button>
                            </div>
                            <button data-bind="event: { click: onClickGenerateCommercialSignals }, disable: !canGenerateCommercialSignals() || isGeneratingCommercialSignals(), text: isGeneratingCommercialSignals() ? 'Generating...' : 'Generate Commercial Signals, Entities, & Values'" class="btn secondary icon i-auto-complete-a poll-generate-signals-btn" type="button">Generate Commercial Signals, Entities, &amp; Values</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="w-form-body w-poll-sections-list">
                <div class="form-body">
                    <div class="form-group">
                        <label class="form-label" data-bind="class: sections().length > 0 ? '' : 'hidden'">Sections</label>
                        <div class="section-list" data-bind="foreach: sections">
                            <button type="button" class="btn-f-s icon i-x-after section-pill" data-bind="text: $data.name + ' (' + $data.length + ')', event: { click: $parent.onClickDeletePollSection }, visible: $data.length === 0"></button>
                            <span class="btn-f-s section-pill" data-bind="text: $data.name + ' (' + $data.length + ')', visible: $data.length > 0"></span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="w-form-body w-poll-sections-list">
                <div class="form-body">
                    <div class="form-group">
                        <label class="form-label">Questions</label>
                        <div class="section-list row poll-questions-toolbar">
                            <button id="${ids.addQuestionButton}" class="btn" data-bind="event: {click: onClickAddPollQuestion}" type="button">+ Add Question</button>
                            <button type="button" class="btn btn--small poll-choices-expand-all" data-bind="visible: questions().length > 0, enable: hasAnyCommercialSignals, click: onClickToggleAllQuestionsCommercial"><span data-bind="text: commercialToggleLabel"></span>${this.COMMERCIAL_ICON}</button>
                        </div>
                    </div>
                </div>
            </div>`;
    }

    /**
     * Draggable question cards with the collapsible commercial signal rows.
     */
    public static questionEditCards(): string {
        return /* html */ `
            <div data-bind="foreach: questions, childrenComplete: handleDragAndDrop($element, questions, '.w-question-card')">
                <div class="w-form-body w-question-card">
                    <div class="form-body w-pinned drag-element" style="background: none" draggable="true">
                        <div class="pinned-form article-pinned entry">
                            <div class="pinned-drag icon i-drag">
                                <span class="pinned-position" data-bind="text: $index() + 1">1</span>
                            </div>
                            <div class="w-btn">
                                <!-- ko if: choices.length > 0 -->
                                <button class="btn border only-icon poll-commercial-toggle" type="button" data-bind="css: { 'is-active': commercialOpen() && $parent.hasCommercialSignals($data) }, disable: !$parent.hasCommercialSignals($data), attr: { title: $parent.getQuestionCommercialToggleLabel($data) }, event: {click: $parent.onClickToggleQuestionCommercial}">
                                    <span class="hidden-text" data-bind="text: $parent.getQuestionCommercialToggleLabel($data)"></span>
                                    ${this.COMMERCIAL_ICON}
                                </button>
                                <!-- /ko -->
                                <button class="btn border only-icon icon border i-edit" type="button" data-bind="event: {click: $parent.onClickEditPollQuestion}">
                                    <span class="hidden-text">Edit</span>
                                </button>
                                <button class="btn border only-icon icon border highlight i-delete" type="button" data-bind="event: {click: $parent.onClickDeletePollQuestion}">
                                    <span class="hidden-text">Delete</span>
                                </button>
                            </div>
                            <div class="w-title">
                                <span class="pinned-title" data-bind="text: question"></span>
                            </div>
                            <ul class="pinned-choices" data-bind="foreach: choices, css: { 'is-commercial-open': commercialOpen }">
                                <li class="poll-pinned-choice">
                                    <span class="poll-pinned-choice-label" data-bind="text: choiceText"></span>
                                    <!-- ko if: $parent.commercialOpen -->
                                    <span class="poll-pinned-commercial" data-bind="foreach: $parents[1].getCommercialSignalParts($data)">
                                        <!-- ko if: $index() > 0 --><span class="poll-pinned-commercial-divider">|</span><!-- /ko -->
                                        <span class="poll-pinned-commercial-part" data-bind="text: $data"></span>
                                    </span>
                                    <!-- /ko -->
                                </li>
                            </ul>

                            <div class="question-section w-btn" data-bind="class: sectionId == 0 ? 'is-hidden' : ''">
                                <strong>Section:</strong>
                                <span data-bind="text: section"></span>
                            </div>

                        </div>
                    </div>
                </div>
            </div>`;
    }

    public static questionLightbox(questionFormHtml: string): string {
        return /* html */ `
            <div data-bind="css: { 'is-visible': isQuestionLightBoxShown }" class="w-lightbox light-theme poll-question-lightbox">
                <div class="lightbox-content">
                    <div class="w-form lightbox-form poll-question-lightbox-form">
                        <div class="pinned-form">
                            <div class="form-header">
                                <h3 class="form-title" data-bind="text: questionOperationTitle"></h3>
                                <button class="close-button icon i-close-2" data-bind="event: { click: onCloseEditedPollQuestion}"></button>
                            </div>
                            <div class="form-body">
                                ${questionFormHtml}
                            </div>
                        </div>
                    </div>
                </div>
            </div>`;
    }
}



// WEBPACK FOOTER //
// ./js/components/templates/pollMarkup.ts